import { Suspense, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { Panel } from '../../panels/panel';
import { PanelTitle } from '../../panels/panel-title';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { useGetCadEventOptions } from '../../federated-search/hooks/use-get-cad-event-options';
import { useFederatedSearchActions } from '../../federated-search/federated-search-context';
import { FOCUSED_FIELD_BY_QUERYABLE_ENTITY_TYPE } from './constants';
import { getUniversalSearchFormLabel } from '~/common/helpers/get-usx-labels';
import { FailedToLoadError } from '../../common/components/failed-to-load-error';
import { lazyLoadFederatedSearchModule } from '../../federated-search/federated-search-remote-monitor';
import { ErrorBoundary } from '../../monitoring/error-boundary';
import { useIsFederatedSearchEnabledButConfirmedUnavailable } from '../../federated-search/federated-search-remote-state-context';

type FederatedSearchQueryFormType = typeof import('federated-search/FederatedSearchQueryForm').FederatedSearchQueryForm;

const FederatedSearchQueryForm = lazyLoadFederatedSearchModule<FederatedSearchQueryFormType>(
    'FederatedSearchQueryForm',
    () => import('federated-search/FederatedSearchQueryForm'),
    (module) => module.FederatedSearchQueryForm as FederatedSearchQueryFormType,
    () => <FailedToLoadError noun="Universal Search" />
);

/**
 *   Displays the Universal Search inquiry form.  This page only displayed when `dexNextEnabled` is true.
 */
export const DexNextFormPanel = observer(function DexNextFormPanel({
    panel,
}: {
    panel: WorkspacePanel;
}): React.ReactElement {
    const dexNextFormPrefill = panel.getPanelState('dexNextFormPrefill');

    const { setQueryFormValues, setQueryFormDataToPrefill, setSelectedForm } =
        useFederatedSearchActions() || {};
    const { getCadEventOptions } = useGetCadEventOptions();
    const isFederatedSearchEnabledButConfirmedUnavailable = useIsFederatedSearchEnabledButConfirmedUnavailable();

    const entityType = dexNextFormPrefill?.entityType;
    const prefillValues = dexNextFormPrefill?.prefillValues;

    const appliedPrefillValuesRef = useRef<typeof prefillValues>();

    useEffect(() => {
        if (setSelectedForm && entityType) {
            setSelectedForm(entityType, FOCUSED_FIELD_BY_QUERYABLE_ENTITY_TYPE[entityType]);
        }
    }, [setSelectedForm, entityType]);

    useEffect(() => {
        if (!prefillValues || appliedPrefillValuesRef.current === prefillValues) {
            return;
        }
        appliedPrefillValuesRef.current = prefillValues;

        setQueryFormDataToPrefill?.(entityType ? { ...prefillValues, entityType } : null);
        setQueryFormValues?.(prefillValues);
    }, [prefillValues, entityType, setQueryFormDataToPrefill, setQueryFormValues]);

    return (
        <Panel
            panel={panel}
            header={<PanelTitle>{getUniversalSearchFormLabel(true)}</PanelTitle>}
            body={
                isFederatedSearchEnabledButConfirmedUnavailable ? (
                    <FailedToLoadError noun="Universal Search" />
                ) : (
                    <ErrorBoundary
                        key={`${entityType ?? ''}-${dexNextFormPrefill?.eventNumber ?? ''}`}
                        name="FEDERATED_SEARCH_QUERY_FORM"
                        extraTags={{ feature: 'federated-search' }}
                        fallback={() => <FailedToLoadError noun="Universal Search" />}
                    >
                        <Suspense fallback={<LoadingIndicator />}>
                            <FederatedSearchQueryForm
                                applicationScope="CAD_DISPATCH"
                                getCadEventOptions={(searchTerm) =>
                                    getCadEventOptions(searchTerm, dexNextFormPrefill?.eventNumber)
                                }
                            />
                        </Suspense>
                    </ErrorBoundary>
                )
            }
        />
    );
});
